import React from "react";
import { CSSTransition } from "react-transition-group";
import getGlobalSettingsAccountMailboxInfo from "owa-account-source-list-store/lib/selectors/getGlobalSettingsAccountMailboxInfo";
import { logUsage } from "owa-analytics";
import { checkMaxEventsPerSessionCount } from "owa-analytics-utils/lib/checkMaxEventsPerSessionCount";
import { isCapabilityEnabled } from "owa-capabilities";
import { shyHeaderCapability } from "owa-capabilities-definitions/lib/shyHeaderCapability";
import { observer } from "owa-mobx-react";
import { useState } from "owa-react";
import { useSideEffect } from "owa-react-hooks/lib/useSideEffect";
import { SHY_HEADER_DEFAULT_ACTIVATION_HEIGHT } from "../../constants/constants";
import { headerEnter, headerEnterActive, headerExit, headerExitActive, headerExitDone, } from "../OwaHeader.scss";
import OwaHeaderBackground from "../OwaHeaderBackground";
import OwaHeaderShyBar from "./OwaHeaderShyBar";

interface OwaShyHeaderWrapperProps {
    children: React.ReactNode;
    waffleButtonRef: React.RefObject<HTMLElement | null>;
}

const SHY_HEADER_ACTIVATION_TIMEOUT = 2000;
const FOCUS_TIMEOUT = 50;
const ANIMATION_TIMEOUT = 180;

const TRANSITION_CLASS_NAMES = {
    enter: headerEnter,
    enterActive: headerEnterActive,
    exit: headerExit,
    exitActive: headerExitActive,
    exitDone: headerExitDone,
} as const;

const defaultExport: React.FunctionComponent<OwaShyHeaderWrapperProps> = observer(function OwaShyHeaderWrapper(props: OwaShyHeaderWrapperProps) {
    const { children, waffleButtonRef } = props;
    
    const enabled = isCapabilityEnabled(shyHeaderCapability);
    
    const [isViewportConstrained, setIsViewportConstrained] = useState(false);
    const [isShyMode, setIsShyMode] = useState(false);
    
    const isShyHeaderEnabled = enabled && isViewportConstrained;
    
    const headerRef = React.useRef<HTMLDivElement>(null);
    const isMouseOverHeaderRef = React.useRef(false);
    const isKeyPressOverHeaderRef = React.useRef(false);
    const isMouseClickShyHeaderRef = React.useRef(false);
    const shyHeaderTimeoutRef = React.useRef<number>(0);
    const focusTimeoutRef = React.useRef<number>(0);
    
    const clearShyHeaderTimeout = React.useCallback(() => {
        if (shyHeaderTimeoutRef.current) {
            window.clearTimeout(shyHeaderTimeoutRef.current);
            shyHeaderTimeoutRef.current = 0;
        }
    }, []);
    
    const clearFocusTimeout = React.useCallback(() => {
        if (focusTimeoutRef.current) {
            window.clearTimeout(focusTimeoutRef.current);
            focusTimeoutRef.current = 0;
        }
    }, []);
    
    const shouldActivateShyMode = React.useCallback((): boolean => {
        return (isShyHeaderEnabled &&
            !isShyMode &&
            !isMouseOverHeaderRef.current &&
            !isKeyPressOverHeaderRef.current);
    }, [isShyHeaderEnabled, isShyMode]);
    
    const deactivateShyMode = React.useCallback((setFocus: boolean = false) => {
        if (!isShyHeaderEnabled || !isShyMode) {
            return;
        }
        
        setIsShyMode(false);
        
        if (setFocus) {
            setTimeout(() => {
                waffleButtonRef.current?.focus();
            }, ANIMATION_TIMEOUT);
        }
    }, [isShyHeaderEnabled, isShyMode, waffleButtonRef]);
    
    const activateShyMode = React.useCallback(() => {
        if (!isShyHeaderEnabled) {
            return;
        }
        
        if (shouldActivateShyMode()) {
            clearShyHeaderTimeout();
            
            shyHeaderTimeoutRef.current = window.setTimeout(() => {
                // Re-check conditions after timeout
                if (shouldActivateShyMode()) {
                    setIsShyMode(true);
                }
            }, SHY_HEADER_ACTIVATION_TIMEOUT);
        }
    }, [isShyHeaderEnabled, shouldActivateShyMode, clearShyHeaderTimeout]);
    
    // Event handlers
    const handleMouseEnter = React.useCallback(() => {
        if (isShyHeaderEnabled) {
            isMouseOverHeaderRef.current = true;
        }
    }, [isShyHeaderEnabled]);
    
    const handleMouseLeave = React.useCallback(() => {
        if (isShyHeaderEnabled) {
            isMouseOverHeaderRef.current = false;
            activateShyMode();
        }
    }, [isShyHeaderEnabled, activateShyMode]);
    
    const handleFocusIn = React.useCallback(() => {
        if (isShyHeaderEnabled) {
            isKeyPressOverHeaderRef.current = true;
            clearFocusTimeout();
        }
    }, [isShyHeaderEnabled, clearFocusTimeout]);
    
    const handleFocusOut = React.useCallback(() => {
        if (!isShyHeaderEnabled) {
            return;
        }
        
        isKeyPressOverHeaderRef.current = false;
        clearFocusTimeout();
        
        focusTimeoutRef.current = window.setTimeout(() => {
            if (!isKeyPressOverHeaderRef.current) {
                activateShyMode();
            }
        }, FOCUS_TIMEOUT);
    }, [isShyHeaderEnabled, activateShyMode, clearFocusTimeout]);
    
    const handleShyBarClick = React.useCallback(() => {
        if (isShyHeaderEnabled) {
            isMouseClickShyHeaderRef.current = true;
        }
    }, [isShyHeaderEnabled]);
    
    const handleShyBarFocus = React.useCallback(() => {
        if (isShyHeaderEnabled) {
            deactivateShyMode(!isMouseClickShyHeaderRef.current);
            isMouseClickShyHeaderRef.current = false;
        }
    }, [isShyHeaderEnabled, deactivateShyMode]);
    
    // Log telemetry once per session when shy mode is activated
    React.useEffect(() => {
        if (isShyHeaderEnabled &&
            isShyMode &&
            checkMaxEventsPerSessionCount("ShyHeaderModeActivated", 1)) {
            logUsage("ShyHeaderModeActivated", getGlobalSettingsAccountMailboxInfo());
        }
    }, [isShyHeaderEnabled, isShyMode]);
    
    // Media query change listener
    useSideEffect("OwaShyHeaderWrapper:mediaQuery", () => {
        if (!enabled) {
            return;
        }
        
        const mediaQuery = window.matchMedia(`(max-height: ${SHY_HEADER_DEFAULT_ACTIVATION_HEIGHT}px)`);
        
        // Set initial state
        setIsViewportConstrained(mediaQuery.matches);
        
        const handleMediaChange = (e: MediaQueryListEvent) => {
            setIsViewportConstrained(e.matches);
            
            if (!e.matches) {
                // Constrained viewport no longer detected, deactivate shy mode
                setIsShyMode(false);
                clearShyHeaderTimeout();
                clearFocusTimeout();
            }
        };
        
        mediaQuery.addEventListener("change", handleMediaChange);
        
        return () => {
            mediaQuery.removeEventListener("change", handleMediaChange);
        };
    }, [enabled, clearShyHeaderTimeout, clearFocusTimeout]);
    
    // Activate shy mode on mount when at constrained viewport
    useSideEffect("OwaShyHeaderWrapper:activateOnMount", () => {
        if (enabled && isViewportConstrained) {
            activateShyMode();
        }
        
        return () => {
            clearShyHeaderTimeout();
            clearFocusTimeout();
        };
    }, [
        enabled,
        isViewportConstrained,
        activateShyMode,
        clearShyHeaderTimeout,
        clearFocusTimeout,
    ]);
    
    return (<>
                {isShyHeaderEnabled && isShyMode && (<OwaHeaderShyBar onMouseDown={handleShyBarClick} onFocus={handleShyBarFocus}/>)}
                <CSSTransition in={isShyHeaderEnabled ? !isShyMode : true} timeout={isShyHeaderEnabled ? 180 : 0} classNames={isShyHeaderEnabled ? TRANSITION_CLASS_NAMES : undefined} unmountOnExit={false} nodeRef={headerRef}>
                    <OwaHeaderBackground ref={headerRef} onMouseEnter={isShyHeaderEnabled ? handleMouseEnter : undefined} onMouseLeave={isShyHeaderEnabled ? handleMouseLeave : undefined} onFocus={isShyHeaderEnabled ? handleFocusIn : undefined} onBlur={isShyHeaderEnabled ? handleFocusOut : undefined}>
                        {children}
                    </OwaHeaderBackground>
                </CSSTransition>
            </>);
}, "OwaShyHeaderWrapper");

export default defaultExport;
